import React, { Context, createContext, PureComponent, ReactNode } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { withErrorBoundary } from "../../error-handling/withErrorBoundary";
import { withTypo3, WithTypo3Props } from "../Typo3Provider/withTypo3";

type PreInitializedUsercentrics = {
  isInitialized?: () => boolean;
  appLoaded: undefined;
};

export type WindowUC_UI = {
  appLoaded: boolean | undefined;
  enableOpenStreetMap: () => void;
  osmEnabled: boolean;
  matomoEnabled: boolean;
  getServicesBaseInfo: () => Array<ServiceBaseInfo>;
  isInitialized: () => boolean;
  restartEmbeddings(): void;
  acceptService: (serviceId: string, consentType?: string) => Promise<{state: string, value: any | undefined}>; //TODO: identify type of value
  reload: () => void;
  restartCMP: () => void;
  closeCMP: () => void;
}

type ServiceBaseInfo = {
    categorySlug: string;
    consent: Object[] & {status: boolean};
    id: string;
    isEssential: boolean;
    isHidden: boolean;
    name: string;
    processorId: string;
    version: string;
}

declare global {
  interface Window {
    UC_UI?: WindowUC_UI;
    UC_UI_SUPPRESS_CMP_DISPLAY?: boolean;
  }
}

const USERCENTRICS_OSM_TEMPLATE_ID = 'j7Igy6o8D';
const USERCENTICS_MATOMO_TEMPLATE_ID = 'u6fxocwTs';

type UsercentricsContextType = Context<WindowUC_UI>;

const defaultContextValue: WindowUC_UI = {
  appLoaded: false,
  reload: () => { },
  enableOpenStreetMap: () => { },
  osmEnabled: false,
  matomoEnabled: false,
  getServicesBaseInfo: () => [],
  isInitialized: () => false,
  restartEmbeddings: () => { },
  acceptService: () => Promise.resolve({state: '', value: undefined}),
  restartCMP: () => {},
  closeCMP: () => {},
};

export const UsercentricsContext: UsercentricsContextType = createContext(
  defaultContextValue
);

type ProviderProps = RouteComponentProps<any, any, any> & WithTypo3Props & {
  children: ReactNode;
};

type ProviderState = {
  osmEnabled: boolean;
  matomoEnabled: boolean;
  usercentricsInitialized: boolean;
  reload: () => void;
  enableOpenStreetMap: () => void;
  appLoaded: boolean | undefined;
  getServicesBaseInfo: () => Array<ServiceBaseInfo>;
  isInitialized: () => boolean;
  restartEmbeddings(): void;
  acceptService: (serviceId: string, consentType?: string) => Promise<{state: string, value: any | undefined}>;
  restartCMP: () => void;
  closeCMP: () => void;
}

class UsercentricsProvider extends PureComponent<ProviderProps & WithTypo3Props, ProviderState> {
  private getRequireUsercentrics() {
    const usercentrics = this.tryGetUsercentrics();
    if (!usercentrics) {
      throw new Error('Usercentrics is required to be ready.');
    }

    return usercentrics;
  }

  private tryGetUsercentrics() {
    if (window.UC_UI === undefined || !window.UC_UI.isInitialized()) {
        return undefined;
    }
    return window.UC_UI;
  }

  public reload = () => {
    // this scans the newly rendered content for special elements like uc-layout="privacySettings" and generates dynamic text
    try {
      const usercentrics = this.tryGetUsercentrics();
      if (usercentrics) {
        usercentrics.restartEmbeddings();
      }
    } catch (e) {
      console.error(e);
    }
  }

  public enableOpenStreetMap = () => {
    try {
      const usercentrics = this.getRequireUsercentrics();
      usercentrics.acceptService(USERCENTRICS_OSM_TEMPLATE_ID);
    } catch (e) {
      console.error(e);
    }
  }

  private updateConsentStatus = () => {
    try {
      const uc = this.getRequireUsercentrics();
      const servicesBaseInfo: ServiceBaseInfo[] | undefined = uc.getServicesBaseInfo();
      if (servicesBaseInfo === undefined) {
          return;
      }
      const osmInfo = servicesBaseInfo.find((service) => service.id === USERCENTRICS_OSM_TEMPLATE_ID);
      const matomoInfo = servicesBaseInfo.find((service) => service.id === USERCENTICS_MATOMO_TEMPLATE_ID);
      this.setState({
        osmEnabled: osmInfo !== undefined ? osmInfo.consent.status : false,
        matomoEnabled: matomoInfo !== undefined ? matomoInfo.consent.status : false,
      });
    } catch (e) {
      console.error(e);
    }
  }

  // tslint:disable-next-line
  public state: ProviderState = {
    osmEnabled: false,
    matomoEnabled: false,
    usercentricsInitialized: false,
    reload: this.reload,
    enableOpenStreetMap: this.enableOpenStreetMap,
    appLoaded: false,
    getServicesBaseInfo: () => [],
    isInitialized: () => false,
    restartEmbeddings: () => { },
    acceptService: () => Promise.resolve({ state: '', value: undefined }),
    restartCMP: () => { },
    closeCMP: () => { }
  }

  public componentDidMount() {
    const onInit = () => {
      this.updateConsentStatus();
      window.addEventListener('usercentricsConsentChanged', this.updateConsentStatus);

      // if (localStorage.getItem('uc_user_interaction') !== 'true' && this.props.typo3.pathContent?.meta.page.usercentricsEnabled) {
      //   window.addEventListener('mousemove', this.whenDocumentReady);
      //   window.addEventListener('touchstart', this.whenDocumentReady);
      // }

      this.setState({usercentricsInitialized: true});
    };

    const uc: WindowUC_UI | PreInitializedUsercentrics = window.UC_UI || { appLoaded: undefined };
    if (uc.isInitialized !== undefined && uc.isInitialized() === true) {
      onInit();
    } else {
      setTimeout(onInit,1500);
    }
  }

  public componentDidUpdate(prevProps: ProviderProps, prevState: ProviderState) {
    if (!this.state.matomoEnabled && prevState.matomoEnabled) {
      window.location.reload();
    }

    if (!this.state.usercentricsInitialized && !prevProps.typo3.pathContent && this.props.typo3.pathContent) {
      this.initializeUsercentrics();
    }

    const ucEnabled = this.props.typo3.pathContent?.meta.page.usercentricsEnabled;
    if (window.UC_UI_SUPPRESS_CMP_DISPLAY !== undefined && ucEnabled) {
        if (ucEnabled === window.UC_UI_SUPPRESS_CMP_DISPLAY) {
            window.UC_UI_SUPPRESS_CMP_DISPLAY = false;
            // const navigator: any = window.navigator; //a bit hacky but TS doesn't know about userActivation yet
            // if (navigator.userActivation.hasBeenActive) {
                this.whenDocumentReady();
            // }
        }
    } else if (window.UC_UI_SUPPRESS_CMP_DISPLAY === false && !ucEnabled) {
        window.UC_UI_SUPPRESS_CMP_DISPLAY = true;
        window.UC_UI?.closeCMP();
    }
  }

  private whenDocumentReady = () => {
    if (
        localStorage.getItem('uc_user_interaction') !== 'true' &&
        this.props.typo3.pathContent?.meta.page.usercentricsEnabled
    ) {
      window.UC_UI?.restartCMP();
      // window.removeEventListener('mousemove', this.whenDocumentReady);
      // window.removeEventListener('touchstart', this.whenDocumentReady);
    }
  };

  private initializeUsercentrics = () => {
    // Initialize usercentrics
    const ucScript = document.querySelector<HTMLScriptElement>('#usercentrics-cmp');
    const ucScriptParent = ucScript ? ucScript.parentElement : null;
    const bodyTag = document.querySelector('body');

    if (!ucScript || !ucScriptParent || !bodyTag) {
      console.error('Could not find and activate usercentrics element.');
      return;
    }

    ucScriptParent.removeChild(ucScript);

    // Reusing the original tag doesn't seem to work for FF so we need to create a new one
    const newUcScript = document.createElement('script');
    newUcScript.src = ucScript.src;
    newUcScript.id = ucScript.id;
    newUcScript.async = true;
    newUcScript.type = 'text/javascript';
    newUcScript.setAttribute('data-settings-id', ucScript.getAttribute('data-settings-id') || '');
    newUcScript.setAttribute('data-language', ucScript.getAttribute('data-language')!);
    if (ucScript.getAttribute('data-version') !== null) {
      newUcScript.setAttribute('data-version', ucScript.getAttribute('data-version') || '');
    }

    ucScriptParent.appendChild(newUcScript);

    // Unfortunately this is a bit ugly. There's a brief moment where the UC modal only contains the consent buttons but not the description text. This results in a quite large Layout Shift which costs points for core web vitals. By waiting for the text to be ready we can circumvent that. #51927
    const hideUsercentricsClass = 'unfinished-usercentrics';
    bodyTag.classList.add(hideUsercentricsClass);

    const checkUsercentricsContent = setInterval(() => {
      const modalElement = document.querySelector<HTMLElement>('#uc-banner-modal');
      const descriptionElement = document.querySelector<HTMLElement>('#uc-full-optin-description');

      let isLoaded = false;

      if (descriptionElement && descriptionElement.innerText) {
        isLoaded = true;
      } else if (modalElement && modalElement.innerText.includes('Daimler Buses')) {
        isLoaded = true;
      } else if (this.state.usercentricsInitialized) {
        isLoaded = true;
      }

      if (isLoaded) {
        clearInterval(checkUsercentricsContent);
        setTimeout(() => {
          bodyTag.classList.remove(hideUsercentricsClass);
        }, 500);
      }
    }, 50);
  };

  public componentWillUnmount() {
    window.removeEventListener('usercentricsConsentChanged', this.updateConsentStatus);
  }

  public render() {
    const {children} = this.props;

    return (
      <UsercentricsContext.Provider value={this.state}>
        {children}
      </UsercentricsContext.Provider>
    );
  }
}

export default withErrorBoundary(
  withRouter(withTypo3(UsercentricsProvider)),
  'UsercentricsProvider'
);
